import { useState, useEffect } from 'react';
import { usePublisherStore } from '../../../store/models/publisher';
import usePublisher from '../../../store/models/publisher/usePublisher';

const SEARCH_DEBOUNCE_MS = 300;

function useDirectorySearch() {
    const publisherStore = usePublisherStore();
    const {
        directoryPublishers,
        searchDirectoryResults,
        directoryFilterIndustries,
        filteredDirectoryPublishers,
        whatsHotPublisherIds,
        search,
        directoryPublishersLoading,
        directoryPublishersLoadingMore,
        directoryHasMore,
        searchHasMore,
        directoryPublishersSearching,
        directoryPublishersSearchingMore,
    } = usePublisher();

    const [searchInputValue, setSearchInputValue] = useState('');

    // Local activeIndustries — updates immediately so pills toggle without
    // waiting for the store observable round-trip
    const [activeIndustries, setActiveIndustries] = useState([]);

    const isSearchActive = !!search.searchText.trim();

    // Debounce searchText — only dispatch to store after 300ms of no typing.
    // setSearchText() alone re-filters whatever's already loaded; the actual
    // server search (EBL-13117) is the separate searchDirectoryPublishers()
    // call below, only fired once there's real text to search for — an
    // emptied box just falls back to the (untouched) browse-mode list via
    // applyDirectoryFilter's own pool switch, no fetch needed.
    useEffect(() => {
        const timer = setTimeout(() => {
            publisherStore.setSearchText(searchInputValue);
            const query = searchInputValue.trim();
            if (query) {
                publisherStore.searchDirectoryPublishers(query);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInputValue]);

    function handleToggleIndustry(industry) {
        const updated = activeIndustries.includes(industry)
            ? activeIndustries.filter((i) => i !== industry)
            : [...activeIndustries, industry];
        setActiveIndustries(updated);
        publisherStore.toggleIndustryFilter(industry);
    }

    function handleClearIndustries() {
        setActiveIndustries([]);
        publisherStore.clearIndustryFilters();
    }

    return {
        industries: directoryFilterIndustries,   // all available — feed into pill list
        activeIndustries,                         // currently toggled — drives active state
        searchInputValue,
        whatshotEnabled: search.whatshotEnabled,
        directoryPublishers,
        // Mode-aware, like the loading/hasMore fields below — the "Showing X
        // of Y" total should reflect whichever pool is actually active, not
        // always the browse-mode one.
        totalCount: isSearchActive ? searchDirectoryResults.length : directoryPublishers.length,
        filteredDirectoryPublishers,
        whatsHotPublisherIds,
        // Mode-aware (EBL-13117) — Directory/DirectoryList stay unaware that
        // browse and search are separate pools; they just get whichever
        // loading/hasMore/onLoadMore applies right now.
        directoryPublishersLoading: isSearchActive ? directoryPublishersSearching : directoryPublishersLoading,
        directoryPublishersLoadingMore: isSearchActive ? directoryPublishersSearchingMore : directoryPublishersLoadingMore,
        directoryHasMore: isSearchActive ? searchHasMore : directoryHasMore,
        onSearchText: (text) => setSearchInputValue(text),
        onToggleWhatshot: (enabled) => publisherStore.setWhatshotEnabled(enabled),
        onToggleIndustry: handleToggleIndustry,
        onClearIndustries: handleClearIndustries,
        // Checked here too (not just inside the actions themselves) — once
        // hasMore is false for whichever mode is active, don't even call the
        // action; VirtuosoGrid's endReached can keep firing on a short list
        // that already fits the viewport.
        onLoadMore: () => {
            if (isSearchActive) {
                if (searchHasMore) publisherStore.loadMoreSearchResults();
            } else if (directoryHasMore) {
                publisherStore.loadMoreDirectoryPublishers();
            }
        },
    };
}

export default useDirectorySearch;
